// @givmo/app-charity — tolerant decoders + plain-language status for the removal-request reads (CPA-WEB7).
//
// GET /charity-admin/charities/{id}/removal-requests returns the NAMED wrapper `CharityRemovalRequestListOut`
// `{ requests:[…] }` (newest first) — the envelope middleware does NOT unwrap it (it is shape-gated on the
// paginated `{data} + meta|links` envelope). So we decode tolerantly via the shared `extractList`
// (`@givmo/api-client/list`) with the `requests` named key (accepts the named wrapper, a bare array, or
// the un-unwrapped `{data}` envelope) and never let a surprising body shape break the page.
//
// STATUS is a plain wire string carrying the removal state machine's OWN vocabulary (never internal grant
// terms — plan D-CP-11 / vocabulary rail). The backend state machine (per the super-admin removal-ops):
//   REQUESTED         → identity-verify pending (OPEN; the first 3-business-day SLA is running)
//   IDENTITY_VERIFIED → processing (OPEN; the second 3-business-day SLA is running)
//   REMOVED           → terminal (the claim was removed; disclosure reverted to the unclaimed posture)
//   CANCELLED         → terminal (the request was cancelled; nothing changed)
// REQUESTED + IDENTITY_VERIFIED are the two OPEN states — an open request replaces the request button with
// the pending status/SLA panel. We surface CHARITY-FACING labels (not the raw SCREAMING_SNAKE tokens).

import { extractList } from '@givmo/api-client/list';
import type { CharityRemovalRequestOut } from '@givmo/api-client';

/** One source for the two-stage removal service-level estimate. */
export const REMOVAL_SLA_COPY =
  'about three business days for the identity check and up to three more to complete it';

/**
 * The caller's removal requests from `GET …/removal-requests` (`CharityRemovalRequestListOut`
 * `{requests:[…]}`), tolerating the named wrapper, a bare array, or the `{data:[…]}` envelope via the
 * shared `extractList`. Returns `[]` for anything unrecognized. Never throws.
 */
export function extractRemovalRequests(body: unknown): CharityRemovalRequestOut[] {
  return extractList<CharityRemovalRequestOut>(body, 'requests');
}

/** The two OPEN removal states (a request still working through the SLA). */
const OPEN_STATUSES = new Set(['REQUESTED', 'IDENTITY_VERIFIED']);

/** Whether a removal request is still open (in-progress) — drives showing the pending panel vs the button. */
export function isOpenRemovalRequest(
  request: CharityRemovalRequestOut | null | undefined,
): boolean {
  return request != null && OPEN_STATUSES.has(request.status);
}

/**
 * The single OPEN request from a (newest-first) list, if any. When an open request exists we show its
 * status + SLA instead of the request button (the backend is idempotent — a second request 409s).
 */
export function openRemovalRequest(
  requests: readonly CharityRemovalRequestOut[],
): CharityRemovalRequestOut | null {
  return requests.find((r) => isOpenRemovalRequest(r)) ?? null;
}

// ── Plain-language status (charity-facing; NOT the raw wire token) ────────────────────────────────────
const REMOVAL_STATUS_LABELS: Record<string, string> = {
  REQUESTED: 'Identity check pending',
  IDENTITY_VERIFIED: 'Processing removal',
  REMOVED: 'Removed',
  CANCELLED: 'Cancelled',
};

/** Readable, charity-facing removal status; '' → '—'; unknown enum → the raw wire value (tolerant). */
export function removalStatusLabel(status: string): string {
  if (status === '') return '—';
  return REMOVAL_STATUS_LABELS[status] ?? status;
}

/** SaaS Badge tone for a removal status; unknown → neutral (tolerant). */
export function removalStatusBadgeTone(
  status: string,
): 'success' | 'info' | 'warning' | 'danger' | 'neutral' {
  switch (status) {
    case 'REMOVED':
      return 'danger';
    case 'CANCELLED':
      return 'neutral';
    case 'IDENTITY_VERIFIED':
      // Identity confirmed; the processing SLA is now running.
      return 'info';
    case 'REQUESTED':
      // Awaiting the identity check — the first SLA is running.
      return 'warning';
    default:
      return 'neutral';
  }
}

/**
 * The next SLA milestone to show a charity for an OPEN request, driven by its status:
 *   REQUESTED         → the identity-verify deadline ("Identity check by")
 *   IDENTITY_VERIFIED → the process deadline ("Removal by")
 * Returns the label + the relevant ISO deadline string (or null if the backend hasn't set it).
 */
export function activeRemovalDeadline(request: CharityRemovalRequestOut): {
  label: string;
  deadline: string | null;
} {
  switch (request.status) {
    case 'REQUESTED':
      return { label: 'Identity check by', deadline: request.identity_verify_deadline ?? null };
    case 'IDENTITY_VERIFIED':
      return { label: 'Removal by', deadline: request.process_deadline ?? null };
    default:
      return { label: 'Deadline', deadline: null };
  }
}

// ── Timestamp formatting ──────────────────────────────────────────────────────────────────────────────
/** `Mon D, YYYY` (UTC) for an SLA deadline / timeline date; returns '—' for a missing/unparseable value. */
export function formatRemovalDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}
